import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';

/**
 * AppLayout
 * Layout for authenticated host screens (Dashboard, Builder, Account)
 * Includes sidebar navigation and top bar with user menu
 */
export default function AppLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navSections = [
    {
      items: [
        { path: '/dashboard', label: 'Quiz', icon: '📚' },
        { path: '/ateliers', label: 'Ateliers', icon: '🎨' },
        { path: '/animations', label: 'Animations', icon: '🎲' }
      ]
    },
    {
      items: [{ path: '/community', label: 'Communauté', icon: '👥' }]
    },
    {
      title: 'CRÉER',
      items: [
        { path: '/quizzes/new', label: 'Créer', icon: '➕' },
        { path: '/quizzes/ai/new', label: 'Créer avec IA', icon: '✨' }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="h-screen flex bg-neutral-50 overflow-hidden">
      {/* Sidebar - Fixed height */}
      <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col h-screen shadow-lg">
        {/* Brand - Fixed height matching header */}
        <div className="h-16 px-6 border-b border-neutral-200 flex items-center flex-shrink-0">
          <Link to="/dashboard" className="flex items-center space-x-3">
            <img src="/animfacile.png" alt="AnimFacile logo" className="h-10 w-auto" />
            <span className="text-xl font-bold text-brand-600">AnimFacile</span>
          </Link>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto" aria-label="Navigation principale">
          {navSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-1">
              {section.title && (
                <div className="px-4 py-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                  {section.title}
                </div>
              )}
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={[
                    'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
                    isActive(item.path)
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  ].join(' ')}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <span aria-hidden="true" className="text-xl">
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* User section - Fixed at bottom */}
        <div className="p-4 border-t border-neutral-200 flex-shrink-0">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-neutral-100 transition-colors text-left"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user?.displayName || user?.email}
                </p>
              </div>
              <span aria-hidden="true">▾</span>
            </button>

            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden">
                <Link
                  to="/account"
                  className="block px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Mon compte
                </Link>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top bar - Fixed height matching sidebar brand */}
        <header className="bg-white border-b border-neutral-200 h-16 flex items-center justify-between px-6 flex-shrink-0">
          <h1 className="text-lg font-semibold text-neutral-900">
            {navSections.flatMap((section) => section.items).find((item) => isActive(item.path))
              ?.label || 'AnimFacile'}
          </h1>
          <div className="flex items-center space-x-4">
            {/* Optional: Launch session button context */}
          </div>
        </header>

        {/* Page content - Scrollable */}
        <main role="main" className="flex-1 overflow-y-auto flex column">
          <div className="p-6 flex-1">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
